<template>
  <div class="search-container">
    <!-- 左侧账号列表 -->
    <UserListSidebar :users="testUsers" :selected-user="currentAccount" title="账号列表" @select-user="selectAccount" />

    <!-- 右侧搜索内容 -->
    <div class="search-content">
      <!-- 未选择账号时的提示 -->
      <div class="no-account-selected" v-if="!currentAccount">
        <div class="no-account-icon">👤</div>
        <div class="no-account-text">请选择左侧账号</div>
        <div class="no-account-subtitle">查看该账号的搜索结果</div>
      </div>

      <!-- 选择账号后的搜索内容 -->
      <template v-else>
        <!-- 页面头部 -->
        <div class="search-header">
          <div class="header-left">
            <h1 class="header-title">{{ currentAccount.name }} - Profiles</h1>
            <p class="header-subtitle">发现有趣的人</p>
          </div>
          <div class="header-actions">
            <!-- 一键点赞按钮 -->
            <button class="batch-like-btn" @click="batchLikeUsers" :disabled="batchLiking || unlikedUsers.length === 0"
              v-if="userList.length > 0">
              <div v-if="batchLiking" class="mini-spinner-white"></div>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              <span>
                {{ batchLiking ? `点赞中 ${batchLikeProgress}/${unlikedUsers.length}` : `一键点赞 (${unlikedUsers.length})` }}
              </span>
            </button>

            <!-- 一键关注按钮 -->
            <button class="batch-follow-btn" @click="batchFollowUsers"
              :disabled="batchFollowing || unfollowedUsers.length === 0" v-if="userList.length > 0">
              <div v-if="batchFollowing" class="mini-spinner-white"></div>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span>
                {{ batchFollowing ? `关注中 ${batchFollowProgress}/${unfollowedUsers.length}` : `一键关注
                (${unfollowedUsers.length})` }}
              </span>
            </button>

            <!-- 筛选按钮 -->
            <button class="filters-btn" @click="showFilters = true">
              <span>Filters</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 4.5H21V6H3V4.5ZM7 11.25H17V12.75H7V11.25ZM9 18H15V19.5H9V18Z" fill="white" />
              </svg>
            </button>
          </div>
        </div>

        <!-- 用户网格 -->
        <div class="user-grid-container" ref="userGridContainer">
          <div class="user-grid" ref="userGrid">
            <div v-for="user in userList" :key="user.id" class="user-card">
              <!-- 用户头像 -->
              <div class="user-avatar">
                <div class="image-container">
                  <img :src="user.avatar_xl || user.avatar_large || user.avatar_small || '/src/assets/logo.png'"
                    :alt="user.name" class="avatar-img" @error="handleImageError" />
                  <div class="image-overlay"></div>
                </div>

                <!-- 在线状态 -->
                <div v-if="user.is_online" class="online-status"></div>

                <div class="action-buttons">
                  <!-- 爱心图标 -->
                  <div class="action-btn heart-btn" :class="user.likedByMe ? 'liked' : 'pointer'"
                    @click="toggleLike(user)">
                    <div class="btn-bg" :class="user.likedByMe ? 'bg-red' : 'bg-black-overlay'">
                      <svg width="20" height="20" viewBox="0 0 24 24" :fill="user.likedByMe ? 'white' : 'none'"
                        stroke="white" stroke-width="2">
                        <path
                          d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </div>
                  </div>
                  <!-- 关注图标 -->
                  <div class="action-btn follow-btn" :class="user.followedByMe ? 'followed' : 'pointer'"
                    @click="toggleFollow(user)">
                    <div class="btn-bg" :class="user.followedByMe ? 'bg-blue' : 'bg-black-overlay'">
                      <svg width="20" height="20" viewBox="0 0 24 24" :fill="user.followedByMe ? 'white' : 'none'"
                        stroke="white" stroke-width="2">
                        <path
                          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                  </div>

                </div>
              </div>

              <!-- 用户信息 -->
              <div class="user-info">
                <div class="user-info-row">
                  <!-- 左侧用户信息 -->
                  <div class="user-name-section">
                    <h3 class="user-name">{{ user.name }}</h3>
                    <div class="user-details">
                      <span v-if="user.age" class="user-age">{{ user.age }}岁</span>
                      <div v-if="user.is_online" class="online-indicator">
                        <div class="online-dot"></div>
                        <span class="online-text">在线</span>
                      </div>
                      <span v-else class="offline-text">离线</span>
                    </div>
                  </div>

                  <!-- 右侧操作按钮 -->
                  <div class="user-actions">
                    <button class="action-btn" :class="user.canRequestChat ? 'outline-btn' : 'filled-btn'"
                      @click="handleUserAction(user)">
                      {{ user.canRequestChat ? '请求聊天' : '查看信息' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 加载更多指示器 -->
            <div v-if="loading" class="loading-indicator">
              <div class="loading-spinner"></div>
              <p>{{ userList.length === 0 ? '正在搜索用户...' : '发现更多有趣的人...' }}</p>
            </div>

            <!-- 没有更多数据 -->
            <div v-if="!hasMore && userList.length > 0" class="no-more">
              <div class="no-more-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <p>已经看完所有用户了</p>
              <p>试试调整筛选条件发现更多人</p>
            </div>

            <!-- 空状态 -->
            <div v-if="!loading && userList.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h3>暂无用户</h3>
              <p>调整筛选条件或稍后再试</p>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 过滤器弹窗 -->
    <div v-if="showFilters" class="fixed top-0 left-0 w100 h100 z-index999" @click="showFilters = false">
      <div class="filter-backdrop absolute top-0 left-0 w100 h100 bg-black opacity-40"></div>
      <div class="filter-content absolute w100 bg-white br-10 pd-24 shadow-modal max-width-600 mg-x-auto" @click.stop>
        <!-- 拖拽指示器 -->
        <div class="drag-indicator w-40 h-4 bg-gray br-2 mg-x-auto mb-20"></div>

        <div class="filter-header flex between middle mb-24">
          <div>
            <h2 class="fs-24 bold color-black mg-0 mb-4">搜索筛选</h2>
            <p class="fs-14 color-gray mg-0">找到最适合的人</p>
          </div>
          <div class="close-btn pointer br-20 w-40 h-40 flex middle center hover-bg-lightgray transition"
            @click="showFilters = false">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#666" stroke-width="2" stroke-linecap="round" />
            </svg>
          </div>
        </div>

        <!-- 城市选择 -->
        <div class="filter-group mb-24">
          <label class="fs-16 bold color-black mb-8 block flex middle">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d7df9" stroke-width="2" class="mr-6">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            来自国家
          </label>
          <div class="select-wrapper relative">
            <select v-model="filters.country"
              class="country-select w100 h-48 pd-x-16 border-2 border-lightgray br-12 fs-14 bg-white transition focus-border-blue">
              <option value="">选择国家</option>
              <option v-for="country in countryList" :key="country.id" :value="country.country_code">
                {{ country.text }}
              </option>
            </select>
          </div>
        </div>

        <!-- 年龄范围 -->
        <div class="filter-group mb-32">
          <label class="fs-16 bold color-black mb-8 block flex middle">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d7df9" stroke-width="2" class="mr-6">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            年龄范围
          </label>
          <div class="age-inputs flex">
            <div class="age-input-group flex1 mr-12">
              <div class="select-wrapper relative">
                <select v-model="filters.ageFrom"
                  class="age-select w100 h-48 pd-x-16 border-2 border-lightgray br-12 fs-14 bg-white transition focus-border-blue">
                  <option v-for="age in ageOptions" :key="'from-' + age" :value="age">{{ age }}</option>
                </select>
              </div>
              <label class="fs-12 color-gray mt-6 block text-center">最小年龄</label>
            </div>
            <div class="to-separator flex middle pd-x-8">
              <div class="separator-line w-16 h-2 bg-lightgray br-1"></div>
            </div>
            <div class="age-input-group flex1 ml-12">
              <div class="select-wrapper relative">
                <select v-model="filters.ageTo"
                  class="age-select w100 h-48 pd-x-16 border-2 border-lightgray br-12 fs-14 bg-white transition focus-border-blue">
                  <option v-for="age in ageOptions" :key="'to-' + age" :value="age">{{ age }}</option>
                </select>
              </div>
              <label class="fs-12 color-gray mt-6 block text-center">最大年龄</label>
            </div>
          </div>
        </div>

        <!-- 性别选择 -->
        <div class="filter-group mb-24" v-if="currentAccount && currentAccount.accountType === 'man'">
          <label class="fs-16 bold color-black mb-8 block flex middle">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d7df9" stroke-width="2" class="mr-6">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            性别偏好
          </label>
          <div class="gender-options">
            <label class="gender-option" :class="{ active: filters.gender === null }">
              <input type="radio" v-model="filters.gender" :value="null" class="gender-radio">
              <span class="gender-text">全部性别</span>
            </label>
            <label class="gender-option" :class="{ active: filters.gender === 'female' }">
              <input type="radio" v-model="filters.gender" value="female" class="gender-radio">
              <span class="gender-text">女性</span>
            </label>
            <label class="gender-option" :class="{ active: filters.gender === 'male' }">
              <input type="radio" v-model="filters.gender" value="male" class="gender-radio">
              <span class="gender-text">男性</span>
            </label>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="filter-actions flex gap-12">
          <button
            class="reset-btn flex1 h-48 bg-white border-2 border-blue color-blue br-24 fs-14 bold pointer transition hover-bg-lightbule2 shadow-btn flex middle center"
            @click="resetFilters" :disabled="loading">
            <svg v-if="!loading" width="16" height="16" viewBox="0 0 24 24" fill="none" class="mr-6">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" />
              <path d="M21 3v5h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" />
              <path d="M3 21v-5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            <div v-if="loading" class="mini-spinner mr-6"></div>
            {{ loading ? '重置中...' : '重置' }}
          </button>
          <button
            class="apply-btn flex1 h-48 bg-blue color-white br-24 fs-14 bold pointer transition hover-bg-blue2 shadow-btn flex middle center"
            @click="applyFilters" :disabled="loading">
            <div v-if="loading" class="mini-spinner mr-6"></div>
            {{ loading ? '搜索中...' : '显示用户' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 用户信息弹窗 -->
    <UserInfo ref="userInfoDialog" />
  </div>
</template>

<script>
import { getAllCountries, getSearchRecommendUsers, addLike, getConnection } from '@/api/talkytimes/search';
import UserListSidebar from '@/components/UserListSidebar.vue';
import UserInfo from '@/views/talkytimes/views/message/components/userInfo.vue';
import { getRequestLimitUsersInfo } from '@/api/talkytimes/message';
import { followUser, unfollowUser } from '@/api/talkytimes/user';

export default {
  name: 'Search',
  components: {
    UserListSidebar,
    UserInfo
  },
  data() {
    return {
      // 账号相关
      currentAccount: null,
      testUsers: [
        {
          id: 1,
          name: 'Test User 1',
          personalAvatar_xl: '/src/assets/profile.png'
        },
        {
          id: 2,
          name: 'Test User 2',
          personalAvatar_xl: '/src/assets/profile.png'
        }
      ],

      // 搜索相关
      userList: [],
      countryList: [],
      showFilters: false,
      loading: false,
      hasMore: true,
      currentPage: 1,
      filters: {
        country: '',
        ageFrom: 18,
        ageTo: 90,
        gender: null // null表示全部，male表示男性，female表示女性
      },
      ageOptions: Array.from({ length: 73 }, (_, i) => i + 18), // 18-90岁

      // 批量点赞相关状态
      batchLiking: false,
      batchLikeProgress: 0,

      // 批量关注相关状态
      batchFollowing: false,
      batchFollowProgress: 0
    };
  },
  computed: {
    // 获取未点赞的用户列表
    unlikedUsers() {
      return this.userList.filter(user => !user.likedByMe);
    },

    // 获取未关注的用户列表
    unfollowedUsers() {
      return this.userList.filter(user => !user.followedByMe);
    }
  },
  async mounted() {
    // 先获取账号列表数据
    await this.$store.dispatch('accountList/fetchAccountList');
    this.initData();
    this.setupScrollListener();
  },
  beforeDestroy() {
    this.removeScrollListener();
  },
  methods: {
    async initData() {
      try {
        // 加载国家列表
        await this.loadCountries();

        // 从store获取账号列表并选择第一个
        const accountList = this.$store.state.accountList.accountList;
        if (accountList && accountList.length > 0) {
          await this.selectAccount(accountList[0]);
        }
      } catch (error) {
        console.error('初始化数据失败:', error);
      }
    },

    // 选择账号
    async selectAccount(account) {
      if (this.currentAccount && this.currentAccount.id === account.id) {
        return; // 已经是当前账号，无需重复选择
      }

      this.currentAccount = account;

      // 设置当前账号的token
      localStorage.setItem('activeUser', JSON.stringify(account));

      // 重置搜索相关状态
      this.userList = [];
      this.currentPage = 1;
      this.hasMore = true;

      // 加载该账号的用户数据
      await this.loadUsers(true);

      // 重新设置滚动监听器
      this.$nextTick(() => {
        this.setupScrollListener();
      });
    },

    async loadCountries() {
      try {
        const response = await getAllCountries({});
        if (response.data) {
          this.countryList = response.data;
        }
      } catch (error) {
        console.error('加载国家列表失败:', error);
      }
    },

    async loadUsers(reset = false) {
      // 如果没有选择账号，不加载用户数据
      if (!this.currentAccount) {
        return;
      }

      if (this.loading && !reset) return;

      // 如果是重置加载，确保显示加载状态
      if (!this.loading) {
        this.loading = true;
      }

      try {
        const params = {
          filters: {
            ageTo: this.filters.ageTo,
            ageFrom: this.filters.ageFrom,
            country: this.filters.country || null,
            gender: this.filters.gender // 添加性别筛选条件
          },
          limit: 12,
          page: reset ? 1 : this.currentPage
        };

        const response = await getSearchRecommendUsers(params);

        if (response.data && response.data.users) {
          // 获取用户ID列表用于查询连接状态
          const userIds = response.data.users.map(user => parseInt(user.id));

          // 查询连接状态
          const connectionResponse = await getConnection({
            idsInterlocutor: userIds
          });
          let requestLimitData = null;
          if (this.currentAccount.accountType == 'man') {
            const requestLimitResponse = await getRequestLimitUsersInfo({
              idsRegularUsers: userIds
            });
            console.log('requestLimitResponse', requestLimitResponse);
            requestLimitData = requestLimitResponse;
          }
          // 使用 for 循环进行数据匹配是否喜欢
          const newUsers = response.data.users.map(user => {
            let matchedConnection = null;

            // 遍历连接数据查找匹配项
            if (connectionResponse && Array.isArray(connectionResponse)) {
              for (let i = 0; i < connectionResponse.length; i++) {
                const connection = connectionResponse[i];
                // 比较 user.id 和 connection.idInterlocutor
                // console.log('数据', user, connection)
                if (user.id == connection.idInterlocutor ||
                  Number(user.id) == Number(connection.idInterlocutor)) {
                  matchedConnection = connection;
                  // console.log(`✅ 匹配成功: 用户 ${user.name} (ID: ${user.id}) 匹配到连接 (idInterlocutor: ${connection.idInterlocutor})`);
                  break;
                }
              }
            }

            if (!matchedConnection) {
              console.log(`❌ 未匹配: 用户 ${user.name} (ID: ${user.id}) 未找到对应连接`);
            }

            // 检查是否可以请求聊天(男用户专属)
            let canRequestChat = false;
            if (requestLimitData && Array.isArray(requestLimitData)) {
              const userLimit = requestLimitData.find(item => item.idRegularUser == user.id);
              if (userLimit && userLimit.restrictions) {
                const chatRequestRestriction = userLimit.restrictions.find(
                  restriction => restriction.type === 'chat_request'
                );
                canRequestChat = chatRequestRestriction ? chatRequestRestriction.isAvailable : false;
              }
            }

            return {
              ...user,
              // 如果找到匹配的连接数据，则使用其值，否则默认为 false
              likedByMe: matchedConnection ? matchedConnection.likedByMe : false,
              visitedByMe: matchedConnection ? matchedConnection.visitedByMe : false,
              winkedByMe: matchedConnection ? matchedConnection.winkedByMe : false,
              blockedByMe: matchedConnection ? matchedConnection.blockedByMe : false,
              followedByMe: matchedConnection ? matchedConnection.followedByMe : false,
              blockedByInterlocutor: matchedConnection ? matchedConnection.blockedByInterlocutor : false,
              // 添加聊天权限标识
              canRequestChat: canRequestChat
            };
          });

          if (reset) {
            this.userList = newUsers;
            this.currentPage = 1;
          } else {
            this.userList.push(...newUsers);
          }

          this.hasMore = newUsers.length === 12;
          this.currentPage++;
        } else {
          this.hasMore = false;
        }
      } catch (error) {
        console.error('加载用户列表失败:', error);
        this.hasMore = false;
      } finally {
        this.loading = false;
      }
    },



    setupScrollListener() {
      // 先移除旧的监听器
      this.removeScrollListener();

      this.scrollHandler = () => {
        // 获取滚动容器
        const scrollContainer = this.$refs.userGridContainer;
        if (!scrollContainer) return;

        const scrollTop = scrollContainer.scrollTop;
        const scrollHeight = scrollContainer.scrollHeight;
        const clientHeight = scrollContainer.clientHeight;

        // 当滚动到底部附近100px时触发加载
        if (scrollTop + clientHeight >= scrollHeight - 100) {
          if (this.hasMore && !this.loading && this.currentAccount) {
            console.log('触发滚动加载更多');
            this.loadUsers();
          }
        }
      };

      // 等待DOM渲染完成后再添加监听器
      this.$nextTick(() => {
        const scrollContainer = this.$refs.userGridContainer;
        if (scrollContainer) {
          scrollContainer.addEventListener('scroll', this.scrollHandler);
          console.log('滚动监听器已添加到容器', {
            scrollHeight: scrollContainer.scrollHeight,
            clientHeight: scrollContainer.clientHeight,
            hasMore: this.hasMore,
            currentAccount: this.currentAccount?.name
          });
        } else {
          console.warn('未找到滚动容器 userGridContainer');
        }
      });
    },

    removeScrollListener() {
      if (this.scrollHandler) {
        const scrollContainer = this.$refs.userGridContainer;
        if (scrollContainer) {
          scrollContainer.removeEventListener('scroll', this.scrollHandler);
          console.log('滚动监听器已移除');
        }
      }
    },

    toggleLike(user) {
      // 如果已经喜欢了，就不允许再次点击
      if (user.likedByMe) {
        return;
      }

      // 设置为喜欢状态
      this.$set(user, 'likedByMe', true);

      // 调用API设置喜欢
      addLike({
        idInterlocutor: user.id
      }).then(res => {
        console.log('设置喜欢成功');
      }).catch(err => {
        console.error('设置喜欢失败:', err);
        // 如果API调用失败，恢复原状态
        this.$set(user, 'likedByMe', false);
      });
    },

    toggleFollow(user) {
      // 切换关注状态
      const newFollowState = !user.followedByMe;

      // 先更新UI状态
      this.$set(user, 'followedByMe', newFollowState);

      // 调用API
      const apiCall = newFollowState ? followUser : unfollowUser;
      apiCall({
        idInterlocutor: user.id
      }).then(res => {
        console.log(newFollowState ? '关注成功' : '取消关注成功');
      }).catch(err => {
        console.error(newFollowState ? '关注失败' : '取消关注失败', err);
        // 如果API调用失败，恢复原状态
        this.$set(user, 'followedByMe', !newFollowState);
      });
    },

    handleUserAction(user) {
      if (user.canRequestChat) {
        // 请求聊天 - 跳转到message页面
        console.log('请求聊天:', user);

        // 构建用户数据，模拟历史对话的格式
        const userData = {
          idInterlocutor: user.id,
          userId: user.id,
          platformUserId: this.currentAccount.id,
          platformUserDetail: {
            id: this.currentAccount.id,
            avatar_large: this.currentAccount.avatar_large,
            avatar_small: this.currentAccount.avatar_small,
            name: this.currentAccount.name,
          },
          userDetail: {
            id: user.id,
            avatar_small: user.avatar_small,
            avatar_large: user.avatar_large,
            name: user.name,
            isVip: user.isVip || false,
            isUnmanagedVip: user.isUnmanagedVip || false,
            is_online: user.is_online || false,
          },
          messagesLeft: 10, // 默认消息数量
        };

        // 跳转到message页面并传递用户数据
        this.$router.push({
          path: '/talkytimes/message',
          query: {
            action: 'startChat',
            userData: JSON.stringify(userData)
          }
        });
      } else {
        // 查看信息
        console.log('查看信息:', user);
        // 调用用户信息组件显示用户详情
        this.$refs.userInfoDialog.init(user.id);
      }
    },

    resetFilters() {
      if (!this.currentAccount) return;

      this.filters = {
        country: '',
        ageFrom: 18,
        ageTo: 90,
        gender: null
      };
      // 重置时先清空列表并显示加载状态
      this.userList = [];
      this.loading = true;
      this.hasMore = true;
      this.currentPage = 1;
      // 延迟执行加载，让用户看到清空效果
      setTimeout(() => {
        this.loadUsers(true);
      }, 100);
    },

    applyFilters() {
      if (!this.currentAccount) return;

      this.showFilters = false;
      // 应用筛选时先清空列表并显示加载状态
      this.userList = [];
      this.loading = true;
      this.hasMore = true;
      this.currentPage = 1;
      // 延迟执行加载，让用户看到清空效果
      setTimeout(() => {
        this.loadUsers(true);
      }, 100);
    },

    handleImageError(event) {
      event.target.src = '/src/assets/logo.png';
    },

    // 批量点赞功能
    async batchLikeUsers() {
      if (this.batchLiking || this.unlikedUsers.length === 0) return;

      this.batchLiking = true;
      this.batchLikeProgress = 0;

      const usersToLike = [...this.unlikedUsers]; // 创建副本，避免在循环中修改原数组

      console.log(`开始批量点赞，共 ${usersToLike.length} 个用户`);

      for (let i = 0; i < usersToLike.length; i++) {
        const user = usersToLike[i];

        try {
          console.log(`正在点赞用户: ${user.name} (${i + 1}/${usersToLike.length})`);

          // 先更新UI状态
          this.$set(user, 'likedByMe', true);
          this.batchLikeProgress = i + 1;

          // 调用API
          await addLike({
            idInterlocutor: user.id
          });

          console.log(`✅ 点赞成功: ${user.name}`);

          // 添加小延迟，避免请求过于频繁
          if (i < usersToLike.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }

        } catch (error) {
          console.error(`❌ 点赞失败: ${user.name}`, error);
          // 如果API调用失败，恢复原状态
          this.$set(user, 'likedByMe', false);
        }
      }

      this.batchLiking = false;
      this.batchLikeProgress = 0;

      console.log('批量点赞完成');

      // 显示完成提示
      this.$nextTick(() => {
        const remainingUnliked = this.unlikedUsers.length;
        if (remainingUnliked === 0) {
          console.log('🎉 所有用户已点赞完成！');
        } else {
          console.log(`完成批量点赞，还有 ${remainingUnliked} 个用户未点赞`);
        }
      });
    },

    // 一键关注功能
    async batchFollowUsers() {
      if (this.batchFollowing || this.unfollowedUsers.length === 0) return;

      this.batchFollowing = true;
      this.batchFollowProgress = 0;

      const usersToFollow = [...this.unfollowedUsers]; // 创建副本，避免在循环中修改原数组

      console.log(`开始批量关注，共 ${usersToFollow.length} 个用户`);

      for (let i = 0; i < usersToFollow.length; i++) {
        const user = usersToFollow[i];

        try {
          console.log(`正在关注用户: ${user.name} (${i + 1}/${usersToFollow.length})`);

          // 先更新UI状态
          this.$set(user, 'followedByMe', true);
          this.batchFollowProgress = i + 1;

          // 调用API
          await followUser({
            idInterlocutor: user.id
          });

          console.log(`✅ 关注成功: ${user.name}`);

          // 添加小延迟，避免请求过于频繁
          if (i < usersToFollow.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }

        } catch (error) {
          console.error(`❌ 关注失败: ${user.name}`, error);
          // 如果API调用失败，恢复原状态
          this.$set(user, 'followedByMe', false);
        }
      }

      this.batchFollowing = false;
      this.batchFollowProgress = 0;

      console.log('批量关注完成');

      // 显示完成提示
      this.$nextTick(() => {
        const remainingUnfollowed = this.unfollowedUsers.length;
        if (remainingUnfollowed === 0) {
          console.log('🎉 所有用户已关注完成！');
        } else {
          console.log(`完成批量关注，还有 ${remainingUnfollowed} 个用户未关注`);
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.search-container {
  display: flex;
  height: calc(100vh - 56px);
  background-color: #f5f7fa;
}

// 左侧账号列表
.account-list-section {
  width: 300px;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;

  .account-list-content {
    flex: 1;
    overflow-y: auto;
    padding: 10px;

    .account-item {
      display: flex;
      align-items: center;
      background: #fff;
      padding: 12px;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      margin-bottom: 5px;
      position: relative;

      &:hover {
        background-color: #f8f9fa;
      }

      &.active {
        background-color: #f0f0ff;
        border: 1px solid #2929c7;
      }

      .account-avatar {
        position: relative;
        margin-right: 12px;
        flex-shrink: 0;

        .avatar-img {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          object-fit: cover;
        }

        .online-status {
          position: absolute;
          bottom: 2px;
          right: 2px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: #0CD85D;
          border: 2px solid #fff;
        }
      }

      .account-info {
        flex: 1;
        min-width: 0;

        .account-name {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .account-status {
          font-size: 12px;
          color: #666;
        }
      }
    }
  }
}

// 右侧搜索内容
.search-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

// 未选择账号状态
.no-account-selected {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;

  .no-account-icon {
    font-size: 64px;
    line-height: 1;
    margin-bottom: 20px;
  }

  .no-account-text {
    font-size: 20px;
    color: #666;
    margin-bottom: 8px;
  }

  .no-account-subtitle {
    font-size: 14px;
    color: #999;
  }
}

.search-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  .header-left {
    .header-title {
      font-size: 24px;
      font-weight: bold;
      color: #1f2937;
      margin: 0 0 4px 0;
    }

    .header-subtitle {
      font-size: 14px;
      color: #6b7280;
      margin: 0;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.user-grid-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  min-height: 0;
}

.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  padding-bottom: 60px;
}

.user-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  .user-avatar {
    position: relative;
    height: 320px;
    overflow: hidden;

    .image-container {
      position: relative;
      height: 100%;
      overflow: hidden;

      .avatar-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: all 0.3s ease;
      }

      .image-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0);
        opacity: 0;
        transition: all 0.3s ease;
      }
    }

    &:hover .image-overlay {
      opacity: 0.2;
    }

    .online-status {
      position: absolute;
      bottom: 12px;
      right: 12px;
      width: 14px;
      height: 14px;
      background: #10b981;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .heart-icon {
      position: absolute;
      top: 12px;
      right: 12px;

      &.liked {
        cursor: default;

        .heart-bg:hover {
          transform: none;
        }
      }

      &.pointer {
        cursor: pointer;
      }

      .heart-bg {
        width: 36px;
        height: 36px;
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(8px);
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.1);
          backdrop-filter: blur(12px);
        }

        &.bg-red {
          background-color: #dc267f;
        }

        &.bg-black-overlay {
          background: rgba(0, 0, 0, 0.4);
        }
      }
    }

    .action-buttons {
      position: absolute;
      top: 12px;
      right: 12px;
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .action-btn {
      width: 36px;
      height: 36px;
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(8px);
      transition: all 0.3s ease;
      cursor: pointer;

      &.followed,
      &.liked {
        cursor: default;

        .btn-bg:hover {
          transform: none;
        }
      }

      &.pointer {
        cursor: pointer;
      }

      .btn-bg {
        width: 100%;
        height: 100%;
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.1);
          backdrop-filter: blur(12px);
        }

        &.bg-blue {
          background-color: #3b82f6;
        }

        &.bg-red {
          background-color: #dc267f;
        }

        &.bg-black-overlay {
          background: rgba(0, 0, 0, 0.4);
        }
      }
    }
  }

  .user-info {
    padding: 20px;

    .user-info-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }

    .user-name-section {
      flex: 1;
      min-width: 0;

      .user-name {
        font-size: 20px;
        font-weight: bold;
        color: #1f2937;
        margin: 0 0 4px 0;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .user-details {
        display: flex;
        align-items: center;
        margin-top: 10px;

        .user-age {
          font-size: 14px;
          color: #6b7280;
          margin-right: 8px;
        }

        .online-indicator {
          display: flex;
          align-items: center;

          .online-dot {
            width: 8px;
            height: 8px;
            background: #10b981;
            border-radius: 50%;
            margin-right: 6px;
          }

          .online-text {
            font-size: 12px;
            color: #10b981;
            font-weight: bold;
          }
        }

        .offline-text {
          font-size: 12px;
          color: #6b7280;
        }
      }
    }

    .user-actions {
      flex-shrink: 0;

      .action-btn {
        height: 44px;
        padding: 0 20px;
        border-radius: 22px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        border: 2px solid transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        &:active {
          transform: translateY(0);
        }

        &.filled-btn {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: white;
          border-color: #8b5cf6;

          &:hover {
            background: linear-gradient(135deg, #7c3aed, #6d28d9);
            box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
          }
        }

        &.outline-btn {
          background: transparent;
          color: #8b5cf6;
          border-color: #8b5cf6;

          &:hover {
            background: rgba(139, 92, 246, 0.1);
            box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
          }
        }
      }
    }
  }
}

.batch-like-btn,
.batch-follow-btn,
.filters-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }
}

.batch-like-btn {
  background: #dc267f;
  color: white;
  box-shadow: 0 4px 12px rgba(220, 38, 127, 0.3);

  &:hover:not(:disabled) {
    background: #c91f72;
    box-shadow: 0 6px 20px rgba(220, 38, 127, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none !important;
  }
}

.batch-follow-btn {
  background: #3b82f6;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);

  &:hover:not(:disabled) {
    background: #2563eb;
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none !important;
  }
}

.filters-btn {
  background: #3b82f6;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);

  &:hover {
    background: #2563eb;
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }
}

// 加载和状态指示器
.loading-indicator,
.no-more,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  grid-column: 1 / -1;

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
  }

  .no-more-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .empty-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
  }
}

.mini-spinner-white {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

// 禁用状态样式
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

// 响应式设计
@media (max-width: 768px) {
  .search-container {
    flex-direction: column;
  }

  .account-list-section {
    width: 100%;
    height: 200px;
  }

  .user-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
  }

  .user-card .user-avatar {
    height: 280px;
  }

  .search-header {
    padding: 16px 20px;
  }

  .header-left .header-title {
    font-size: 20px;
  }
}

.filter-content {
  max-height: 85vh;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-sizing: border-box;
  top: 130px;
  right: 28px;
}

.shadow-modal {
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
}

.drag-indicator {
  background: rgba(0, 0, 0, 0.1);
}

.select-wrapper {
  &::after {
    content: '';
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #666;
    pointer-events: none;
    transition: transform 0.2s ease;
  }
}

.country-select,
.age-select {
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2d7df9 !important;
    box-shadow: 0 0 0 4px rgba(45, 125, 249, 0.1);
  }

  &:hover {
    border-color: #2d7df9;
  }
}

.focus-border-blue:focus {
  border-color: #2d7df9 !important;
  box-shadow: 0 0 0 4px rgba(45, 125, 249, 0.1);
}

.gap-12 {
  gap: 12px;
}

.max-width-600 {
  max-width: 600px;
}

.mg-x-auto {
  margin-left: auto;
  margin-right: auto;
}

.gender-options {
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 400px;
}

.gender-option {
  flex: 1;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  .gender-radio {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .gender-text {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.3s ease;
    user-select: none;
    padding: 0 16px;
  }

  &:hover .gender-text {
    border-color: #2d7df9;
    background: rgba(45, 125, 249, 0.05);
    color: #2d7df9;
  }

  &.active .gender-text {
    background: linear-gradient(135deg, #2d7df9, #1e40af);
    border-color: #2d7df9;
    color: white;
    box-shadow: 0 2px 8px rgba(45, 125, 249, 0.25);
  }

  .gender-radio:focus+.gender-text {
    box-shadow: 0 0 0 3px rgba(45, 125, 249, 0.1);
  }
}

.loading-indicator,
.no-more,
.empty-state {
  grid-column: 1 / -1;
}

@media (max-width: 768px) {
  .user-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
    padding: 0 20px 40px;
  }

  .search-header {
    padding: 16px 20px;
  }

  .age-inputs {
    flex-direction: column;
    gap: 16px;
  }

  .age-input-group {
    margin: 0 !important;
  }

  .to-separator {
    display: none;
  }

  .filter-content {
    padding: 20px;
    border-radius: 20px 20px 0 0;
  }

  .gender-options {
    flex-direction: column;
    gap: 8px;
  }

  .gender-option .gender-text {
    height: 48px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .user-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 16px;
    padding: 0 16px 32px;
  }

  .search-header {
    padding: 12px 16px;
  }

  .header-left h1 {
    font-size: 24px;
  }

  .filters-btn {
    padding: 8px 12px;
    font-size: 14px;
  }

  .user-card {
    &:hover {
      transform: translateY(-4px) scale(1.01);
    }
  }

  .user-avatar {
    height: 280px;
  }
}

// 额外的动画效果
.user-card {
  transform: translateY(30px);
  opacity: 0;
  animation: fadeInUp 0.4s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 为不同的卡片添加延迟动画
@for $i from 1 through 12 {
  .user-card:nth-child(#{$i}) {
    animation-delay: #{$i * 0.1}s;
  }
}

// 滚动条美化
.filter-content::-webkit-scrollbar {
  width: 6px;
}

.filter-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.filter-content::-webkit-scrollbar-thumb {
  background: rgba(45, 125, 249, 0.3);
  border-radius: 3px;

  &:hover {
    background: rgba(45, 125, 249, 0.5);
  }
}

// 对话框居中
.middleDialog::v-deep.el-dialog__wrapper {
  top: 5vh;
  // height: 90vh;

  .el-dialog {
    // height: 100%;

    .el-dialog__body {
      height: calc(90vh - 5px);
      box-sizing: border-box;
    }
  }
}
</style>